/**
 * MedicationForm — add or edit a medication.
 *
 * Owner-friendly design:
 *  - Auto-detects category and route from drug name where possible
 *  - Uses plain-language labels for routes and categories
 *  - Groups fields logically: what → how much/often → when/who → details
 *  - Category and route shown as auto-detected confirmation, with option to change
 */

import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { useTranslation, Trans } from 'react-i18next';
import type { MedicationFormData, MedicationCategory, MedicationRoute, Medication } from './types';
import { CATEGORY_LABELS, ROUTE_LABELS, FREQUENCY_OPTIONS, detectDrugInfo, frequencyLabel } from './types';
import { parseDose, doseForInput } from './dose';
import { Button } from '../shared/Button';
import { DateField } from '../shared/DateField';
import { TimePickerDropdown } from '../shared/TimePickerDropdown';
import { useRegisterUnsavedChanges } from '../shared/UnsavedChangesGuard';

interface MedicationFormProps {
  onSubmit: (data: MedicationFormData) => Promise<void>;
  onCancel: () => void;
  /** If provided, pre-fills the form for editing */
  initialData?: Medication | null;
}

const EMPTY_FORM: MedicationFormData = {
  drug_name: '',
  dose: '',
  dose_unit: 'mg',
  frequency: 'once_daily',
  route: 'oral',
  category: 'other',
  start_date: format(new Date(), 'yyyy-MM-dd'),
  prescribed_by: '',
  notes: '',
  reminder_time: null,
};

function medicationToFormData(med: Medication): MedicationFormData {
  return {
    drug_name: med.drug_name,
    dose: doseForInput(med.dose, med.dose_unit),
    dose_unit: med.dose_unit || 'mg',
    frequency: med.frequency,
    route: med.route,
    category: med.category,
    start_date: med.start_date,
    prescribed_by: med.prescribed_by || '',
    notes: med.notes || '',
    reminder_time: med.reminder_time || null,
  };
}

export const MedicationForm: React.FC<MedicationFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<MedicationFormData>(
    initialData ? medicationToFormData(initialData) : EMPTY_FORM
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The dose's own inline message. Shown once the owner leaves the field (or
  // tries to save), so it does not flash while "1 1/2" is half typed.
  const [doseError, setDoseError] = useState<string | null>(null);
  const [autoDetected, setAutoDetected] = useState(false);
  const [showCategoryRoute, setShowCategoryRoute] = useState(!!initialData);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isEditing = !!initialData;

  // Unsaved-changes guard: dirty whenever the form differs from what it was seeded
  // with. On a successful submit the parent navigates back and this form unmounts,
  // which clears the registration automatically.
  const initialFormRef = useRef<MedicationFormData>(
    initialData ? medicationToFormData(initialData) : EMPTY_FORM,
  );
  useRegisterUnsavedChanges(
    JSON.stringify(formData) !== JSON.stringify(initialFormRef.current),
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Auto-detect category and route when drug name changes
  useEffect(() => {
    if (isEditing) return; // Don't auto-detect when editing

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      const detected = detectDrugInfo(formData.drug_name);
      if (detected) {
        setFormData((prev) => ({
          ...prev,
          category: detected.category,
          route: detected.route,
          // Default the unit to mL only for confidently-liquid drugs (e.g.
          // Semintra, Elura). Otherwise leave the owner's current unit so we
          // never silently overwrite a deliberate choice for tablet drugs.
          ...(detected.dose_unit ? { dose_unit: detected.dose_unit } : {}),
        }));
        setAutoDetected(true);
      } else if (formData.drug_name.trim()) {
        // User typed something we don't recognise — keep current values
        // but clear the auto-detected flag
        setAutoDetected(false);
      } else {
        setAutoDetected(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [formData.drug_name, isEditing]);

  // Synchronous re-entry guard. This is a real <form> with a type="submit"
  // button, so a double-Enter (or Enter+click) can re-fire onSubmit before the
  // button re-renders to disabled. The ref flips immediately, so the second call
  // bails and we never POST a duplicate medication.
  const submittingRef = useRef(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    setError(null);

    if (!formData.drug_name.trim()) {
      setError(t('medications.form.drugNameRequired'));
      return;
    }
    if (!formData.start_date) {
      setError(t('medications.form.startDateRequired'));
      return;
    }
    const dose = parseDose(formData.dose);
    if (!dose.ok) {
      setDoseError(dose.message);
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('medications.form.saveFailed'));
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">
        {isEditing ? t('medications.form.editHeading') : t('medications.form.addHeading')}
      </h3>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* ── What: Drug name ── */}
      <div>
        <label htmlFor="med-drug-name" className="block text-sm font-medium text-gray-700 mb-1">
          {t('medications.form.nameLabel')} <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="med-drug-name"
          name="drug_name"
          value={formData.drug_name}
          onChange={handleChange}
          placeholder={t('medications.form.namePlaceholder')}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-sage-500 focus:border-sage-500"
          autoComplete="off"
          required
        />
        <p className="text-xs text-gray-600 mt-1">
          {t('medications.form.nameHint')}
        </p>
      </div>

      {/* Auto-detected category + route confirmation */}
      {autoDetected && !showCategoryRoute && (
        <div className="rounded-lg bg-sage-50 border border-sage-200 p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="text-sm">
              <p className="text-sage-800">
                <Trans
                  i18nKey="medications.form.detected"
                  values={{ category: CATEGORY_LABELS[formData.category] }}
                  components={{ label: <span className="font-medium" /> }}
                />
              </p>
              <p className="text-sage-600 text-xs mt-0.5">
                {ROUTE_LABELS[formData.route]}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowCategoryRoute(true)}
              className="text-xs text-sage-600 hover:text-sage-800 underline whitespace-nowrap"
            >
              {t('medications.form.change')}
            </button>
          </div>
        </div>
      )}

      {/* Manual category + route (shown when: editing, user clicked "Change", or drug not recognised) */}
      {(showCategoryRoute || (!autoDetected && formData.drug_name.trim().length > 2)) && (
        <div className="space-y-3 rounded-lg bg-gray-50 border border-gray-200 p-3">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">
            {autoDetected ? t('medications.form.overrideType') : t('medications.form.chooseType')}
          </p>
          <div>
            <label htmlFor="med-category" className="block text-xs font-medium text-gray-600 mb-1">
              {t('medications.form.categoryLabel')}
            </label>
            <select
              id="med-category"
              name="category"
              value={formData.category}
              onChange={(e) => {
                handleChange(e);
                setAutoDetected(false);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-sage-500 focus:border-sage-500 bg-white"
            >
              {(Object.entries(CATEGORY_LABELS) as [MedicationCategory, string][]).map(
                ([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                )
              )}
            </select>
            <p className="text-xs text-gray-600 mt-1">
              {t('medications.form.categoryHint')}
            </p>
          </div>
          <div>
            <label htmlFor="med-route" className="block text-xs font-medium text-gray-600 mb-1">
              {t('medications.form.routeLabel')}
            </label>
            <select
              id="med-route"
              name="route"
              value={formData.route}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-sage-500 focus:border-sage-500 bg-white"
            >
              {(Object.entries(ROUTE_LABELS) as [MedicationRoute, string][]).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* ── How much / how often ── */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="med-dose" className="block text-sm font-medium text-gray-700 mb-1">
            {t('medications.form.doseLabel')}
          </label>
          <input
            type="text"
            id="med-dose"
            name="dose"
            value={formData.dose}
            onChange={(e) => {
              handleChange(e);
              // Clear a stale message as soon as the value becomes readable.
              if (doseError && parseDose(e.target.value).ok) setDoseError(null);
            }}
            onBlur={(e) => {
              const parsed = parseDose(e.target.value);
              setDoseError(parsed.ok ? null : parsed.message);
            }}
            placeholder={t('medications.form.dosePlaceholder')}
            inputMode="text"
            aria-invalid={doseError ? true : undefined}
            aria-describedby={doseError ? 'med-dose-error' : undefined}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-sage-500 focus:border-sage-500 ${
              doseError ? 'border-red-400' : 'border-gray-300'
            }`}
          />
          {doseError && (
            <p id="med-dose-error" role="alert" className="text-xs text-red-700 mt-1">
              {doseError}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="med-dose-unit" className="block text-sm font-medium text-gray-700 mb-1">
            {t('medications.form.unitLabel')}
          </label>
          <select
            id="med-dose-unit"
            name="dose_unit"
            value={formData.dose_unit}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-sage-500 focus:border-sage-500 bg-white"
          >
            <option value="mg">{t('medications.units.mg')}</option>
            <option value="ml">{t('medications.units.ml')}</option>
            <option value="mcg">{t('medications.units.mcg')}</option>
            <option value="units">{t('medications.units.unitsOption')}</option>
            <option value="tablets">{t('medications.units.tabletsOption')}</option>
            <option value="capsules">{t('medications.units.capsulesOption')}</option>
            <option value="drops">{t('medications.units.dropsOption')}</option>
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="med-frequency" className="block text-sm font-medium text-gray-700 mb-1">
          {t('medications.form.frequencyLabel')} <span className="text-red-500">*</span>
        </label>
        <select
          id="med-frequency"
          name="frequency"
          value={formData.frequency}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-sage-500 focus:border-sage-500 bg-white"
          required
        >
          {FREQUENCY_OPTIONS.map((freq) => (
            <option key={freq} value={freq}>
              {frequencyLabel(freq)}
            </option>
          ))}
        </select>
      </div>

      {/* Reminder time */}
      <div>
        <label className="flex items-center gap-2 mb-2">
          <input
            type="checkbox"
            checked={formData.reminder_time !== null}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                reminder_time: e.target.checked ? '09:00' : null,
              }));
            }}
            className="w-4 h-4 border border-gray-300 rounded focus:ring-2 focus:ring-sage-500"
          />
          <span className="text-sm font-medium text-gray-700">{t('medications.form.remindMe')}</span>
        </label>
        {formData.reminder_time !== null && (
          /* The same select-based 12-hour control Settings uses for the default
             medication reminder time — this one overrides it for one drug, so
             the two must not be different kinds of control. */
          <TimePickerDropdown
            ariaLabel={t('medications.form.reminderTimeLabel')}
            value={formData.reminder_time}
            onChange={(t) => setFormData((prev) => ({ ...prev, reminder_time: t }))}
          />
        )}
      </div>

      {/* ── When / who ── */}
      <div className="grid grid-cols-2 gap-3">
        <DateField
          label={t('medications.form.startedLabel')}
          name="start_date"
          value={formData.start_date}
          onChange={(v) => setFormData((prev) => ({ ...prev, start_date: v }))}
          range="happened"
          required
        />
        <div>
          <label htmlFor="med-prescribed-by" className="block text-sm font-medium text-gray-700 mb-1">
            {t('medications.form.prescribedByLabel')}
          </label>
          <input
            type="text"
            id="med-prescribed-by"
            name="prescribed_by"
            value={formData.prescribed_by}
            onChange={handleChange}
            placeholder={t('medications.form.prescribedByPlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-sage-500 focus:border-sage-500"
          />
        </div>
      </div>

      {/* Notes */}
      <div>
        <label htmlFor="med-notes" className="block text-sm font-medium text-gray-700 mb-1">
          {t('medications.form.notesLabel')}
        </label>
        <textarea
          id="med-notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={2}
          placeholder={t('medications.form.notesPlaceholder')}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-sage-500 focus:border-sage-500 resize-none"
        />
      </div>

      {/* Action buttons */}
      <div className="flex gap-3 pt-2">
        <Button
          type="submit"
          isLoading={isSubmitting}
          className="flex-1"
        >
          {isEditing ? t('medications.form.saveChanges') : t('medications.form.addHeading')}
        </Button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
        >
          {t('medications.form.cancel')}
        </button>
      </div>
    </form>
  );
};

export default MedicationForm;
